import { openZip } from './zip';
import { parseTranscript, type ParsedMessage } from './parser';
import { mimeForFilename } from './media';
import { chatNameFrom } from './chatname';

/**
 * Reading an export without an account, and without uploading a byte.
 *
 * The point is to answer "is this any good?" with the person's own
 * conversation rather than a demo, before asking them for anything. It is also
 * the most honest privacy claim the product can make, because here it is
 * literally true that nothing left the device: no account, no key, no request.
 *
 * Only the transcript is read up front. Media stays in the zip and is pulled
 * out one file at a time as rows scroll into view — the chat list is
 * virtualised, so a handful are live at once and a 3.5 GB export costs no more
 * memory than a small one.
 */
/**
 * What was in the zip and what the transcript asked for. Two rounds of guessing
 * at why an iPhone export showed no pictures is what this exists to end: it
 * reports counts, extensions and unmatched names — never message text — so a
 * person can say what they are seeing without sending their conversation.
 */
export interface PreviewReport {
  filesInZip: number;
  referenced: number;
  matched: number;
  /** Referenced by the transcript but absent from the zip. */
  missing: string[];
  /** In the zip but never referenced — usually nothing, sometimes revealing. */
  orphans: string[];
  extensions: Record<string, number>;
  omitted: number;
  sample: string[];
}

export interface Preview {
  title: string;
  messages: ParsedMessage[];
  mediaCount: number;
  report: PreviewReport;
  /** An object URL for one attachment, or null if the zip has no such file. */
  resolve: (name: string) => Promise<string | null>;
  /** Release every object URL handed out. */
  dispose: () => void;
  file: File;
}

export async function openPreview(file: File): Promise<Preview> {
  const zip = await openZip(file);
  const transcript = zip.entries.find((e) => e.name.toLowerCase().endsWith('.txt'));
  if (!transcript) throw new Error('No transcript (.txt) inside — is this a WhatsApp export?');

  const text = new TextDecoder().decode(await zip.read(transcript));
  const { messages } = parseTranscript(text);

  // Android names the transcript after the chat; iOS always calls it
  // `_chat.txt`, which is how a conversation ended up displayed as "_chat".
  // The zip's own name carries the title in both cases.
  const title = chatNameFrom(transcript.name, file.name);

  const byName = new Map(zip.entries.map((e) => [e.name.replace(/^.*\//, ''), e]));
  const urls = new Map<string, string>();

  const referenced = new Set<string>();
  for (const m of messages) if (m.file) referenced.add(m.file.replace(/^.*\//, ''));
  const missing = [...referenced].filter((n) => !byName.has(n));
  const used = new Set([...referenced].filter((n) => byName.has(n)));
  const extensions: Record<string, number> = {};
  for (const e of zip.entries) {
    if (e === transcript) continue;
    const ext = (e.name.split('.').pop() ?? '(none)').toLowerCase();
    extensions[ext] = (extensions[ext] ?? 0) + 1;
  }
  const report: PreviewReport = {
    filesInZip: zip.entries.length - 1,
    referenced: referenced.size,
    matched: used.size,
    missing: missing.slice(0, 5),
    orphans: zip.entries
      .filter((e) => e !== transcript && !referenced.has(e.name.replace(/^.*\//, '')))
      .slice(0, 5).map((e) => e.name),
    extensions,
    omitted: messages.filter((m) => m.omitted).length,
    sample: zip.entries.filter((e) => e !== transcript).slice(0, 5).map((e) => e.name),
  };

  const resolve = async (name: string) => {
    const cached = urls.get(name);
    if (cached) return cached;
    const entry = byName.get(name.replace(/^.*\//, ''));
    if (!entry) return null;
    const bytes = await zip.read(entry);
    // The declared type is not decoration: WebKit refuses to decode bytes that
    // disagree with it, where Chrome sniffs and carries on.
    const url = URL.createObjectURL(new Blob([bytes as BlobPart], {
      type: mimeForFilename(name),
    }));
    urls.set(name, url);
    return url;
  };

  return {
    title,
    messages,
    mediaCount: zip.entries.length - 1,
    report,
    resolve,
    dispose: () => { for (const u of urls.values()) URL.revokeObjectURL(u); urls.clear(); },
    file,
  };
}


